<template>
  <div
    v-if="inventoryService.state.isOpen && inventoryService.state.lootingTargetId !== null"
    class="external-inventory-overlay"
    @click="closeLooting"
  >
    <div
      class="external-inventory-container"
      @click.stop
    >
      <!-- Player Inventory (Left) -->
      <div class="inventory-column">
        <InventoryPanel
          v-if="playerEntityId !== undefined"
          :entity-id="playerEntityId"
          :inventory-service="inventoryService"
          :asset-manager="assetManager"
          :services="services"
          :title="'Your Inventory'"
          :show-close="false"
          :readonly="false"
          :intent="intent"
        />
      </div>

      <!-- Divider -->
      <div class="inventory-divider">
        <div class="divider-line" />
        <div class="divider-icon">
          ⇄
        </div>
        <div class="divider-line" />
      </div>

      <!-- External Inventory (Right) -->
      <div class="inventory-column">
        <InventoryPanel
          v-if="externalTargetId !== null"
          :entity-id="externalTargetId"
          :inventory-service="inventoryService"
          :asset-manager="assetManager"
          :services="services"
          :title="externalPanelTitle"
          :show-close="true"
          :readonly="false"
          :intent="intent"
          @close="closeLooting"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, watch } from 'vue';

import { AssetManager } from '../AssetManager';
import { ClientServiceContainer } from '../services/ClientServiceContainer';
import { InventoryUIService } from '../services/InventoryUIService';
import InventoryPanel from './InventoryPanel.vue';

const props = defineProps<{
  inventoryService: InventoryUIService;
  assetManager: AssetManager;
  services: ClientServiceContainer;
}>();

const playerEntityId = computed(() => props.inventoryService.getPlayerEntityId());
const externalTargetId = computed(() => props.inventoryService.state.lootingTargetId);

// Determine if this is pickpocketing (target is alive) or looting (target is dead)
const isPickpocketing = computed(() => {
  if (externalTargetId.value === null) return false;
  const world = props.inventoryService.getWorld();
  return !world.hasComponent(externalTargetId.value, 'Dead');
});

const intent = computed(() => (isPickpocketing.value ? 'pickpocket' : 'normal'));

const externalPanelTitle = computed(() => {
  if (externalTargetId.value === null) return '';
  const world = props.inventoryService.getWorld();
  const displayName = world.getComponent(externalTargetId.value, 'DisplayName');
  const name = displayName ? displayName.name : '';

  if (isPickpocketing.value) {
    return name ? `Pickpocketing ${name}` : 'Pickpocketing';
  } else {
    // Falls back to "Corpse" only if no name can be found
    if (name) {
      return `Corpse of ${name}`;
    }
    return 'Corpse';
  }
});

// Auto-close on aggro
watch(
  () => externalTargetId.value,
  () => {
    // In a real implementation with a reactive ECS, we would watch for the Aggro component.
    // This is now handled by the ExternalUIAggroSystem which runs in the game loop
    // and closes the UI if the target entity gains an Aggro component.
  },
  { immediate: true },
);

function closeLooting() {
  props.inventoryService.close();
}

function handleKeydown(event: KeyboardEvent) {
  if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
    return;
  }

  if (event.code === 'Escape' && props.inventoryService.isLooting()) {
    props.inventoryService.close();
    event.stopPropagation();
  }
}

onMounted(() => {
  window.addEventListener('keydown', handleKeydown);
});

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown);
});
</script>

<style scoped>
.external-inventory-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.75);
  z-index: 1000;
  pointer-events: auto;
  backdrop-filter: blur(4px);
}

.external-inventory-container {
  display: flex;
  align-items: flex-start;
  gap: 32px;
  animation: slide-up 0.3s ease-out;
}

@keyframes slide-up {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.inventory-column {
  display: flex;
  flex-direction: column;
}

.inventory-divider {
  display: flex;
  flex-direction: column;
  align-items: center;
  align-self: center;
  height: 200px;
  gap: 16px;
}

.divider-line {
  width: 2px;
  flex-grow: 1;
  background: linear-gradient(to bottom, transparent, rgba(100, 140, 180, 0.4), transparent);
}

.divider-icon {
  color: rgba(100, 140, 180, 0.8);
  font-size: 32px;
  text-shadow: 0 0 10px rgba(100, 140, 180, 0.4);
}
</style>
